import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PartnerSection from '../components/PartnerSection';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [projects, setProjects] = useState<any[]>([]);

  const placeholderProjects = [
    { id: -1, title: 'Modern Villa', category: 'Civil & Interior', location: 'Chennai', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&q=80', description: 'Luxury residential villa with contemporary design and premium interiors.' },
    { id: -2, title: 'Corporate Office', category: 'Interior Design', location: 'Bangalore', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80', description: 'Modern workspace with ergonomic design and brand-aligned aesthetics.' },
    { id: -3, title: 'Boutique Hotel', category: 'Civil & Interior', location: 'Coimbatore', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80', description: 'End-to-end construction and luxury interior for a boutique hospitality space.' },
    { id: -4, title: 'Retail Showroom', category: 'Interior Design', location: 'Madurai', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=600&q=80', description: 'Brand-forward interior design for a premium retail experience.' },
    { id: -5, title: 'Apartment Complex', category: 'Civil Works', location: 'Chennai', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80', description: 'Multi-unit residential complex with modern amenities and quality construction.' },
    { id: -6, title: 'Restaurant & Cafe', category: 'Interior Design', location: 'Pondicherry', imageData: null, imageMime: null, img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=80', description: 'Atmospheric dining space blending heritage elements with modern comfort.' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    api.get('/portfolio').then(res => {
      setProjects(res.data.length > 0 ? res.data : placeholderProjects);
    }).catch(() => setProjects(placeholderProjects));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    // Require at least one contact method so the architect can actually reach back.
    if (!formData.phone && !formData.email) {
      setSubmitError('Please provide a phone number or email.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/enquiries', {
        name: formData.name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        service: formData.service || undefined,
        message: formData.message,
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Could not send enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { value: '150+', label: 'Projects Completed' },
    { value: '12+', label: 'Years Experience' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '50+', label: 'Expert Team' },
  ];

  return (
    <div className="landing">

      {/* NAVBAR */}
      <nav className={`lp-nav ${scrolled ? 'lp-nav--scrolled' : ''}`}>
        <div className="lp-nav__inner">
          <div className="lp-nav__logo" onClick={() => scrollTo('hero')}>
            <span className="lp-nav__logo-icon">A</span>
            <span className="lp-nav__logo-text">Arcivi</span>
          </div>
          <ul className={`lp-nav__links ${menuOpen ? 'lp-nav__links--open' : ''}`}>
            {['about', 'services', 'projects', 'contact'].map(s => (
              <li key={s}><button onClick={() => scrollTo(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button></li>
            ))}
            <li><button onClick={() => scrollTo('partner')}>Partner with Us</button></li>
            <li><button className="lp-nav__cta" onClick={() => scrollTo('contact')}>Get a Quote</button></li>
          </ul>
          <button className="lp-nav__burger" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section id="hero" className="lp-hero">
        <div className="lp-hero__overlay" />
        <div className="lp-hero__content">
          <p className="lp-hero__tag">Civil & Interior Design Experts</p>
          <h1 className="lp-hero__title">Crafting Spaces<br />That Inspire</h1>
          <p className="lp-hero__sub">From structural foundations to exquisite interiors — we bring your vision to life with precision, creativity, and excellence.</p>
          <div className="lp-hero__actions">
            <button className="lp-btn lp-btn--primary" onClick={() => scrollTo('projects')}>View Our Work</button>
            <button className="lp-btn lp-btn--outline" onClick={() => scrollTo('contact')}>Contact Us</button>
          </div>
        </div>
        <div className="lp-hero__scroll" onClick={() => scrollTo('about')}>
          <span>Scroll</span>
          <div className="lp-hero__scroll-line" />
        </div>
      </section>

      {/* STATS */}
      <section className="lp-stats">
        {stats.map((s, i) => (
          <div key={i} className="lp-stats__item">
            <span className="lp-stats__value">{s.value}</span>
            <span className="lp-stats__label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* ABOUT */}
      <section id="about" className="lp-about lp-section">
        <div className="lp-container">
          <div className="lp-about__grid">
            <div className="lp-about__img-wrap">
              <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=700&q=80" alt="Arcivi team at work" />
              <div className="lp-about__badge">Est. 2012</div>
            </div>
            <div className="lp-about__text">
              <p className="lp-section__tag">About Us</p>
              <h2 className="lp-section__title">Building Dreams with <span>Precision & Passion</span></h2>
              <p>Arcivi is a full-service architectural and interior design firm based in Tamil Nadu. We specialise in civil construction and interior design for residential, commercial, and hospitality projects across South India.</p>
              <p>Our integrated approach means one team handles everything — from architectural planning and structural engineering to furniture selection and finishing touches. This ensures seamless execution and consistent quality at every stage.</p>
              <ul className="lp-about__list">
                <li>✓ End-to-end project management</li>
                <li>✓ Licensed structural engineers on team</li>
                <li>✓ Transparent pricing & timelines</li>
                <li>✓ 5-year construction warranty</li>
              </ul>
              <button className="lp-btn lp-btn--primary" onClick={() => scrollTo('contact')}>Work With Us</button>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="lp-services lp-section">
        <div className="lp-container">
          <p className="lp-section__tag">What We Do</p>
          <h2 className="lp-section__title">Our <span>Services</span></h2>
          <p className="lp-section__sub">Comprehensive solutions for every stage of your project</p>
          <div className="lp-services__grid">

            <div className="lp-service-card lp-service-card--civil">
              <div className="lp-service-card__icon">🏗️</div>
              <h3>Civil Works</h3>
              <p>Complete construction services from foundation to finishing, built to last.</p>
              <ul>
                <li>Architectural Design & Planning</li>
                <li>Structural Engineering</li>
                <li>Residential Construction</li>
                <li>Commercial Buildings</li>
                <li>Renovations & Extensions</li>
                <li>Site Supervision & Management</li>
              </ul>
            </div>

            <div className="lp-service-card lp-service-card--interior">
              <div className="lp-service-card__icon">🛋️</div>
              <h3>Interior Design</h3>
              <p>Thoughtful, beautiful interiors tailored to your style and lifestyle.</p>
              <ul>
                <li>Residential Interiors</li>
                <li>Office & Commercial Spaces</li>
                <li>Modular Kitchen & Wardrobes</li>
                <li>False Ceiling & Lighting</li>
                <li>Furniture & Décor Sourcing</li>
                <li>3D Visualisation & Planning</li>
              </ul>
            </div>

            <div className="lp-service-card lp-service-card--consult">
              <div className="lp-service-card__icon">📐</div>
              <h3>Consultation</h3>
              <p>Expert advice to make your project a success from day one.</p>
              <ul>
                <li>Site Analysis & Feasibility</li>
                <li>Budget Planning & Estimation</li>
                <li>Material Selection Guidance</li>
                <li>Vastu & Space Planning</li>
                <li>Permit & Approval Assistance</li>
                <li>Project Scheduling</li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="lp-projects lp-section">
        <div className="lp-container">
          <p className="lp-section__tag">Portfolio</p>
          <h2 className="lp-section__title">Our <span>Recent Work</span></h2>
          <p className="lp-section__sub">A glimpse of the spaces we have transformed</p>
          <div className="lp-projects__grid">
            {projects.map((p, i) => {
              const imgSrc = p.imageData
                ? `data:${p.imageMime};base64,${p.imageData}`
                : p.imageUrl || p.img || '';
              return (
                <div key={p.id || i} className="lp-project-card">
                  <div className="lp-project-card__img">
                    {imgSrc && <img src={imgSrc} alt={p.title} />}
                    <div className="lp-project-card__overlay">
                      <p>{p.description || p.desc}</p>
                    </div>
                  </div>
                  <div className="lp-project-card__info">
                    <span className="lp-project-card__cat">{p.category}</span>
                    <h4>{p.title}</h4>
                    <span className="lp-project-card__loc">📍 {p.location}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="lp-why lp-section">
        <div className="lp-container">
          <p className="lp-section__tag">Why Choose Us</p>
          <h2 className="lp-section__title">The Arcivi <span>Difference</span></h2>
          <div className="lp-why__grid">
            {[
              { icon: '🎯', title: 'Single Point of Contact', desc: 'One team, one contact — no coordination chaos between architects, contractors, and designers.' },
              { icon: '💎', title: 'Premium Quality', desc: 'We use only certified materials and work with vetted contractors to guarantee lasting quality.' },
              { icon: '⏱️', title: 'On-Time Delivery', desc: 'We respect your time. Our project management system keeps every milestone on track.' },
              { icon: '💰', title: 'Transparent Pricing', desc: 'Detailed quotations with no hidden costs. Know exactly what you are paying for.' },
            ].map((w, i) => (
              <div key={i} className="lp-why__card">
                <span className="lp-why__icon">{w.icon}</span>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER WITH US */}
      <PartnerSection />

      {/* CONTACT */}
      <section id="contact" className="lp-contact lp-section">
        <div className="lp-container">
          <div className="lp-contact__grid">
            <div className="lp-contact__info">
              <p className="lp-section__tag">Get In Touch</p>
              <h2 className="lp-section__title">Start Your <span>Project Today</span></h2>
              <p>Ready to build or redesign? Tell us about your project and we will get back to you within 24 hours.</p>
              <div className="lp-contact__details">
                <div className="lp-contact__detail">
                  <span>📍</span>
                  <div><strong>Office</strong><p>Chennai, Tamil Nadu, India</p></div>
                </div>
                <div className="lp-contact__detail">
                  <span>📞</span>
                  <div><strong>Phone</strong><p>+91 99430 79965</p></div>
                </div>
                <div className="lp-contact__detail">
                  <span>✉️</span>
                  <div><strong>Email</strong><p>hello@arcivi.com</p></div>
                </div>
                <div className="lp-contact__detail">
                  <span>🕐</span>
                  <div><strong>Working Hours</strong><p>Mon – Sat: 9:00 AM – 6:00 PM</p></div>
                </div>
              </div>
            </div>
            <div className="lp-contact__form-wrap">
              {submitted ? (
                <div className="lp-contact__success">
                  <span>✅</span>
                  <h3>Thank You!</h3>
                  <p>We have received your enquiry and will contact you within 24 hours.</p>
                  <button className="lp-btn lp-btn--primary" onClick={() => setSubmitted(false)}>Send Another</button>
                </div>
              ) : (
                <form className="lp-contact__form" onSubmit={handleSubmit}>
                  <h3>Free Consultation</h3>
                  {submitError && <div className="lp-contact__error">{submitError}</div>}
                  <div className="lp-form__row">
                    <input required placeholder="Your Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    <input type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="lp-form__row">
                    <input placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                    <select value={formData.service} onChange={e => setFormData({...formData, service: e.target.value})}>
                      <option value="">Select Service</option>
                      <option>Civil Construction</option>
                      <option>Interior Design</option>
                      <option>Civil & Interior (Both)</option>
                      <option>Consultation Only</option>
                    </select>
                  </div>
                  <textarea required rows={4} placeholder="Tell us about your project..." value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} />
                  <button type="submit" className="lp-btn lp-btn--primary lp-btn--full" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send Enquiry →'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer__grid">
            <div className="lp-footer__brand">
              <div className="lp-nav__logo">
                <span className="lp-nav__logo-icon">A</span>
                <span className="lp-nav__logo-text" style={{color:'white'}}>Arcivi</span>
              </div>
              <p>Crafting exceptional spaces through expert civil construction and interior design across South India.</p>
            </div>
            <div className="lp-footer__links">
              <h4>Quick Links</h4>
              <ul>
                {['about', 'services', 'projects', 'contact'].map(s => (
                  <li key={s}><button onClick={() => scrollTo(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button></li>
                ))}
              </ul>
            </div>
            <div className="lp-footer__links">
              <h4>Services</h4>
              <ul>
                <li><span>Civil Construction</span></li>
                <li><span>Interior Design</span></li>
                <li><span>Modular Kitchens</span></li>
                <li><span>Project Consultation</span></li>
              </ul>
            </div>
            <div className="lp-footer__links">
              <h4>Contact</h4>
              <ul>
                <li><span>+91 99430 79965</span></li>
                <li><span>hello@arcivi.com</span></li>
                <li><span>Chennai, Tamil Nadu</span></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer__bottom">
            <p>© {new Date().getFullYear()} Arcivi. All rights reserved.</p>
            <button className="lp-footer__login" onClick={() => window.location.href = '/login'}>Staff Login</button>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
